import { useEffect, useRef, useState } from 'react';

interface PhoneMockupImageProps {
  type?: 'image';
  image: string;
  videoSrc?: never;
  alt?: string;
  className?: string;
}

interface PhoneMockupVideoProps {
  type: 'video';
  videoSrc: string;
  image?: never;
  alt?: string;
  className?: string;
}

type PhoneMockupProps = PhoneMockupImageProps | PhoneMockupVideoProps;

export default function PhoneMockup(props: PhoneMockupProps) {
  const { alt = 'App screenshot', className = '' } = props;
  const scrollRef = useRef<HTMLDivElement>(null);
  const innerRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const rafRef = useRef<number>(0);
  const posRef = useRef(0);
  const [isVisible, setIsVisible] = useState(false);

  const isVideo = props.type === 'video';

  useEffect(() => {
    const el = isVideo ? videoRef.current?.parentElement : scrollRef.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => setIsVisible(entry.isIntersecting),
      { threshold: 0.3 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [isVideo]);

  // Video play/pause on visibility
  useEffect(() => {
    if (!isVideo) return;
    const video = videoRef.current;
    if (!video) return;
    if (isVisible) {
      video.play().catch(() => {});
    } else {
      video.pause();
    }
  }, [isVisible, isVideo]);

  // Image auto-scroll logic
  useEffect(() => {
    if (isVideo || !isVisible) return;

    const inner = innerRef.current;
    const zone = scrollRef.current;
    if (!inner || !zone) return;

    const speed = 0.5;
    let paused = true;
    let direction: 'down' | 'up' = 'down';

    const initialDelay = setTimeout(() => {
      paused = false;
    }, 1500);

    function tick() {
      if (!paused && inner && zone) {
        const maxScroll = Math.max(0, inner.offsetHeight - zone.offsetHeight);
        if (maxScroll > 0) {
          if (direction === 'down') {
            posRef.current += speed;
            if (posRef.current >= maxScroll) {
              posRef.current = maxScroll;
              paused = true;
              inner.style.transform = `translateY(-${posRef.current}px)`;
              setTimeout(() => {
                direction = 'up';
                paused = false;
              }, 1500);
            } else {
              inner.style.transform = `translateY(-${posRef.current}px)`;
            }
          } else {
            posRef.current -= speed;
            if (posRef.current <= 0) {
              posRef.current = 0;
              paused = true;
              inner.style.transform = 'translateY(0)';
              setTimeout(() => {
                direction = 'down';
                paused = false;
              }, 1500);
            } else {
              inner.style.transform = `translateY(-${posRef.current}px)`;
            }
          }
        }
      }
      rafRef.current = requestAnimationFrame(tick);
    }

    rafRef.current = requestAnimationFrame(tick);
    return () => {
      clearTimeout(initialDelay);
      cancelAnimationFrame(rafRef.current);
    };
  }, [isVisible, isVideo]);

  return (
    <div className={`flex justify-center ${className}`}>
      <div className="phone-mockup">
        <div className="phone-notch" />
        <div className="phone-btn-r" style={{ height: 40, top: 100 }} />
        <div className="phone-btn-r" style={{ height: 40, top: 150 }} />
        <div className="phone-btn-l" style={{ height: 60, top: 120 }} />
        <div className="phone-screen">
          {isVideo ? (
            <video
              ref={videoRef}
              src={props.videoSrc}
              muted
              loop
              playsInline
              aria-label={alt}
              style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', borderRadius: 'inherit' }}
            />
          ) : (
            <>
              <div className="phone-status-bar">
                <span>14:06</span>
                <div className="phone-status-icons">
                  <span>●●●</span>
                  <span>WiFi</span>
                  <span>🔋</span>
                </div>
              </div>
              <div className="phone-scroll-zone" ref={scrollRef}>
                <div className="phone-scroll-inner" ref={innerRef}>
                  <img src={props.image} alt={alt} />
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
